import type { CfEntry } from '~/types/contentful'

import { CfHeroSection } from './hero/CfHeroSection'
import { CfFeatureRowSection } from './feature-row/CfFeatureRowSection'
import { CfCardGridSection } from './card-grid/CfCardGridSection'
import { CfLogoStripSection } from './logostrip/CfLogoStripSection'
import { CfTestimonialSection } from './testimonial/CfTestimonialSection'
import { CfStepFlowSection } from './step-flow/CfStepFlowSection'
import { CfCtaBandSection } from './cta-band/CfCtaBandSection'
import { CfFaqsSection } from './faqs/CfFaqsSection'
import { CfGuidesSection } from './guides/CfGuidesSection'

// Section-specific CSS imports as URL
import cfSectionShellCssHref from './cf-section-shell.css?url'
import cfHeroSectionCssHref from './hero/cf-hero-section.css?url'
import cfLogoSectionCssHref from './logostrip/cf-logostrip-section.css?url'
import cfStepFlowSectionCssHref from './step-flow/cf-step-flow-section.css?url'
import cfFeatureRowSectionCssHref from './feature-row/cf-feature-row-section.css?url'
import cfTestimonialSectionCssHref from './testimonial/cf-testimonial-section.css?url'
import cfCardGridSectionCssHref from './card-grid/cf-card-grid-section.css?url'
import cfCtaBandSectionCsshref from './cta-band/cf-cta-band-section.css?url'
import cfFaqsSectionCssHref from './faqs/cf-faqs-section.css?url'
import cfGuidesSectionCssHref from './guides/cf-guides-section.css?url'
import inlineAnimatedSvgCssHref from './inline-animated-svg.css?url'
import typewriterInputCssHref from '~/components-v2/typewriter-input/typewriterInput.css?url'
import postCollectionCssHref from '~/components-v2/post-collection/post.css?url'
import { styles as componentStyles } from '~/components-v2'

export { cfSectionShellCssHref }

/**
 * Map of Contentful content type ID → CSS href
 * Used by the route's `links()` to load only the CSS needed for sections on the page.
 */
export const sectionCssMap: Record<string, string | string[]> = {
  // CfMedia can inline animated SVG assets in these sections
  heroSection: [
    cfHeroSectionCssHref,
    typewriterInputCssHref,
    inlineAnimatedSvgCssHref,
  ],
  logoStripSection: cfLogoSectionCssHref,
  featureRowSection: [cfFeatureRowSectionCssHref, inlineAnimatedSvgCssHref],
  testimonialSection: [
    cfTestimonialSectionCssHref,
    componentStyles.linkCtaNext,
  ],
  cardGridSection: [cfCardGridSectionCssHref, postCollectionCssHref],
  stepFlowSection: [cfStepFlowSectionCssHref, inlineAnimatedSvgCssHref],
  ctaBandSection: cfCtaBandSectionCsshref,
  faqsSection: cfFaqsSectionCssHref,
  guidesSection: [cfGuidesSectionCssHref, componentStyles.linkCtaNext],
}

export const contentfulSectionLinks = () => {
  const hrefs = [
    cfSectionShellCssHref,
    ...Object.values(sectionCssMap).flatMap((hrefOrHrefs) =>
      Array.isArray(hrefOrHrefs) ? hrefOrHrefs : [hrefOrHrefs],
    ),
  ]

  return Array.from(new Set(hrefs))
    .filter(Boolean)
    .map((href) => ({ rel: 'stylesheet' as const, href }))
}

/**
 * Render sections based on their Contentful content type ID.
 */
export function renderSections(sections: CfEntry[]) {
  if (!Array.isArray(sections)) return null

  return sections.map((section, index) => {
    const typeId = section?.sys?.contentType?.sys?.id
    const key = section?.sys?.id ?? `section-${index}`

    if (!typeId) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          '[renderSections] Invalid section payload at index:',
          index,
        )
      }
      return null
    }

    // as any hacky just to get lint to work for now - eric
    switch (typeId) {
      case 'heroSection':
        return <CfHeroSection key={key} {...section} />

      case 'logoStripSection':
        return <CfLogoStripSection key={key} {...section} />

      case 'featureRowSection':
        return <CfFeatureRowSection key={key} {...section} />

      case 'cardGridSection':
        return <CfCardGridSection key={key} {...section} />

      case 'stepFlowSection':
        return <CfStepFlowSection key={key} {...section} />

      case 'ctaBandSection':
        return <CfCtaBandSection key={key} {...section} />

      case 'faqsSection':
        return <CfFaqsSection key={key} {...section} />

      case 'testimonialSection':
        return <CfTestimonialSection key={key} {...section} />

      case 'guidesSection':
        return <CfGuidesSection key={key} {...section} />

      default: {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[renderSections] Unhandled section type:', typeId)
        }
        return null
      }
    }
  })
}
